/**
 * PostHog Analytics Integration
 *
 * Provides analytics tracking via PostHog, only enabled in production.
 * Integrates with Clerk for user identification.
 *
 * @module lib/posthog
 */

import { useEffect } from 'react';
import posthog, { type PostHog } from 'posthog-js';
import { PostHogProvider as PHProvider, usePostHog } from 'posthog-js/react';
import { useUser } from '@clerk/clerk-react';

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST as string | undefined;

// Only enable PostHog in production with valid config
const isPostHogEnabled = import.meta.env.PROD && !!POSTHOG_KEY;

// Initialize PostHog only in production
if (isPostHogEnabled && POSTHOG_KEY) {
  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST || 'https://eu.i.posthog.com',
    person_profiles: 'identified_only',
    capture_pageview: true,
    capture_pageleave: true,
    autocapture: true,
    // Respect Do Not Track
    respect_dnt: true,
    // Disable in localhost even if PROD is true (for preview builds testing)
    loaded: (ph: PostHog) => {
      if (import.meta.env.DEV) {
        ph.opt_out_capturing();
      }
    },
  });
}

/**
 * PostHog Provider wrapper
 * Only renders PostHogProvider in production, passes children through in development
 */
export function PostHogProvider({ children }: { children: React.ReactNode }) {
  if (!isPostHogEnabled) {
    return children;
  }

  return <PHProvider client={posthog}>{children}</PHProvider>;
}

/**
 * Hook to identify user with PostHog when authenticated
 * Call this in a component that has access to Clerk user
 */
export function usePostHogIdentify() {
  const { user, isSignedIn } = useUser();
  const ph = isPostHogEnabled ? posthog : null;

  useEffect(() => {
    if (!ph) return;

    if (isSignedIn && user) {
      // Identify user with PostHog
      ph.identify(user.id, {
        email: user.primaryEmailAddress?.emailAddress,
        name: user.fullName,
        firstName: user.firstName,
        lastName: user.lastName,
        createdAt: user.createdAt,
      });
    } else {
      // Reset when user signs out
      ph.reset();
    }
  }, [ph, isSignedIn, user]);
}

/**
 * Hook to capture custom events
 * Returns a no-op function in development
 */
export function useCapture() {
  const ph = usePostHog();

  if (!isPostHogEnabled || !ph) {
    // Return no-op functions in development
    return {
      capture: (_event: string, _properties?: Record<string, unknown>) => {},
      captureException: (_error: Error, _properties?: Record<string, unknown>) => {},
    };
  }

  return {
    capture: (event: string, properties?: Record<string, unknown>) => {
      ph.capture(event, properties);
    },
    captureException: (error: Error, properties?: Record<string, unknown>) => {
      ph.capture('$exception', {
        $exception_message: error.message,
        $exception_type: error.name,
        $exception_stack_trace_raw: error.stack,
        ...properties,
      });
    },
  };
}

// Re-export usePostHog for direct access when needed
export { usePostHog };
